import { TrackingInfo } from '@shopee-user/tracking';

// utils
import { getOTPEnterVerificationPageTypeAndSchema } from '@shopee-user/business-customization-logic';

// constants
import { OTPEnterVerificationTrackingPoint } from '@shopee-user/tracking-constants';
import { TrackingOperation } from '@shopee-user/tracking';
import { config } from '@shopee-user/platform';
import { BusinessTarget } from '@shopee-user/global';

// types
import {
  TrackingDataOTPProps,
  TrackingDataFieldOTP,
  TrackingDataOtpVerificationResultProps,
  TrackingDataFieldOtpVerificationResult,
  TrackingDataPhoneAlreadyUsedProps,
  TrackingDataFieldPhoneAlreadyUsed,
  TrackingDataEmailAlreadyUsedProps,
  TrackingDataFieldEmailAlreadyUsed,
} from './types';

export function generateTrackingOTPEnterVerificationView({
  scenario,
}: TrackingDataOTPProps): TrackingInfo<TrackingDataFieldOTP> {
  return {
    operation: TrackingOperation.View,
    data: { scenario },
    ...getOTPEnterVerificationPageTypeAndSchema(
      OTPEnterVerificationTrackingPoint.PageView
    ),
  };
}

export function generateTrackingOTPEnterVerificationResendButtonClick({
  scenario,
}: TrackingDataOTPProps): TrackingInfo<TrackingDataFieldOTP> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'resend_button',
    data: { scenario },
    ...getOTPEnterVerificationPageTypeAndSchema(
      OTPEnterVerificationTrackingPoint.ResendButtonClick
    ),
  };
}

export function generateTrackingOTPEnterVerificationDifferentMethodButtonClick({
  scenario,
}: TrackingDataOTPProps): TrackingInfo<TrackingDataFieldOTP> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'different_method_button',
    data: { scenario },
    ...getOTPEnterVerificationPageTypeAndSchema(
      OTPEnterVerificationTrackingPoint.DifferentMethodButtonClick
    ),
  };
}

export function generateTrackingOTPEnterVerificationNextButtonClick({
  scenario,
}: TrackingDataOTPProps): TrackingInfo<TrackingDataFieldOTP> {
  const targetType =
    config.BUSINESS_TARGET === BusinessTarget.FoodDriver
      ? 'ok_button'
      : 'next_button';
  return {
    operation: TrackingOperation.Click,
    target_type: targetType,
    data: { scenario },
    ...getOTPEnterVerificationPageTypeAndSchema(
      OTPEnterVerificationTrackingPoint.NextButtonClick
    ),
  };
}

export function generateTrackingOTPEnterVerificationHelpButtonClick({
  scenario,
}: TrackingDataOTPProps): TrackingInfo<TrackingDataFieldOTP> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'help_button',
    data: { scenario },
    ...getOTPEnterVerificationPageTypeAndSchema(
      OTPEnterVerificationTrackingPoint.HelpButtonClick
    ),
  };
}

export function generateTrackingOTPEnterVerificationBackButtonClick({
  scenario,
}: TrackingDataOTPProps): TrackingInfo<TrackingDataFieldOTP> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'back_button',
    data: { scenario },
    ...getOTPEnterVerificationPageTypeAndSchema(
      OTPEnterVerificationTrackingPoint.BackButtonClick
    ),
  };
}

export function generateTrackingOTPEnterVerificationResult({
  scenario,
  hasError,
}: TrackingDataOtpVerificationResultProps): TrackingInfo<
  TrackingDataFieldOtpVerificationResult
> {
  return {
    operation: TrackingOperation.OtpVerifyAction,
    data: { scenario, verification_result: hasError ? 0 : 1 },
    ...getOTPEnterVerificationPageTypeAndSchema(
      OTPEnterVerificationTrackingPoint.VerificationResult
    ),
  };
}

export function generateTrackingPhoneAlreadyUsedView({
  scenario,
}: TrackingDataPhoneAlreadyUsedProps): TrackingInfo<
  TrackingDataFieldPhoneAlreadyUsed
> {
  return {
    operation: TrackingOperation.View,
    data: { scenario },
    page_section: 'phone_already_used_popup',
    ...getOTPEnterVerificationPageTypeAndSchema(
      OTPEnterVerificationTrackingPoint.PhoneAlreadyUsedView
    ),
  };
}

export function generateTrackingPhoneAlreadyUsedOkButtonClick({
  scenario,
}: TrackingDataPhoneAlreadyUsedProps): TrackingInfo<
  TrackingDataFieldPhoneAlreadyUsed
> {
  return {
    operation: TrackingOperation.Click,
    data: { scenario },
    target_type: 'ok_button',
    page_section: 'phone_already_used_popup',
    ...getOTPEnterVerificationPageTypeAndSchema(
      OTPEnterVerificationTrackingPoint.PhoneAlreadyUsedOkButtonClick
    ),
  };
}

export function generateTrackingPhoneAlreadyUsedContactUsButtonClick({
  scenario,
}: TrackingDataPhoneAlreadyUsedProps): TrackingInfo<
  TrackingDataFieldPhoneAlreadyUsed
> {
  return {
    operation: TrackingOperation.Click,
    data: { scenario },
    target_type: 'contact_us_button',
    page_section: 'phone_already_used_popup',
    ...getOTPEnterVerificationPageTypeAndSchema(
      OTPEnterVerificationTrackingPoint.PhoneAlreadyUsedContactUsButtonClick
    ),
  };
}

export function generateTrackingEmailAlreadyUsedView({
  scenario,
}: TrackingDataEmailAlreadyUsedProps): TrackingInfo<
  TrackingDataFieldEmailAlreadyUsed
> {
  return {
    operation: TrackingOperation.View,
    data: { scenario },
    page_section: 'email_already_used_popup',
    ...getOTPEnterVerificationPageTypeAndSchema(
      OTPEnterVerificationTrackingPoint.EmailAlreadyUsedView
    ),
  };
}

export function generateTrackingEmailAlreadyUsedOkButtonClick({
  scenario,
}: TrackingDataEmailAlreadyUsedProps): TrackingInfo<
  TrackingDataFieldEmailAlreadyUsed
> {
  return {
    operation: TrackingOperation.Click,
    data: { scenario },
    target_type: 'ok_button',
    page_section: 'email_already_used_popup',
    ...getOTPEnterVerificationPageTypeAndSchema(
      OTPEnterVerificationTrackingPoint.EmailAlreadyUsedOkButtonClick
    ),
  };
}

export function generateTrackingEmailAlreadyUsedContactUsButtonClick({
  scenario,
}: TrackingDataEmailAlreadyUsedProps): TrackingInfo<
  TrackingDataFieldEmailAlreadyUsed
> {
  return {
    operation: TrackingOperation.Click,
    data: { scenario },
    target_type: 'contact_us_button',
    page_section: 'email_already_used_popup',
    ...getOTPEnterVerificationPageTypeAndSchema(
      OTPEnterVerificationTrackingPoint.EmailAlreadyUsedContactUsButtonClick
    ),
  };
}

export function generateTrackingAccountBannedImpression({
  scenario,
}: TrackingDataOTPProps): TrackingInfo<TrackingDataFieldOTP> {
  return {
    operation: TrackingOperation.Impression,
    page_section: 'suspended_account_pop_up',
    data: { scenario },
    ...getOTPEnterVerificationPageTypeAndSchema(
      OTPEnterVerificationTrackingPoint.AccountBannedPopupView
    ),
  };
}

export function generateTrackingAccountBannedOkButtonClick({
  scenario,
}: TrackingDataOTPProps): TrackingInfo<TrackingDataFieldOTP> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'ok_button',
    page_section: 'suspended_account_pop_up',
    data: { scenario },
    ...getOTPEnterVerificationPageTypeAndSchema(
      OTPEnterVerificationTrackingPoint.AccountBannedPopupOkButtonClick
    ),
  };
}

export function generateTrackingAccountBannedContactUsButtonClick({
  scenario,
}: TrackingDataOTPProps): TrackingInfo<TrackingDataFieldOTP> {
  return {
    operation: TrackingOperation.Click,
    target_type: 'contact_us',
    page_section: 'suspended_account_pop_up',
    data: { scenario },
    ...getOTPEnterVerificationPageTypeAndSchema(
      OTPEnterVerificationTrackingPoint.AccountBannedPopupContactUsButtonClick
    ),
  };
}
